/*
 * Product Form
 * Simple Fencing enhanced version
 * Version: v1.1.1 (Robust Payload & Form ID Fixes)
 */

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');

      if (document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.error = false;
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');

      const formData = new FormData(this.form);
      const productVariantId = formData.get('id');

      let requestConfig;
      let isMultiItem = false;

      try {
        const panelPayload = this.getPanelConfiguratorPayload(formData);

        if (panelPayload && panelPayload.items && panelPayload.items.length > 0) {
          requestConfig = this.buildMultiItemRequestConfig(formData, panelPayload);
          isMultiItem = true;
        } else {
          requestConfig = this.buildDefaultRequestConfig(formData);
        }
      } catch (error) {
        this.error = true;
        this.handleErrorMessage(error.message || 'Please check the selected options.');
        this.resetSubmitButton();
        return;
      }

      let fetchUrl = routes.cart_add_url;
      // Ensure we hit the strict .js endpoint when sending JSON arrays
      if (isMultiItem && !fetchUrl.endsWith('.js')) {
        fetchUrl += '.js';
      }

      fetch(fetchUrl, requestConfig)
        .then((response) => response.json())
        .then((response) => {
          if (response.status && response.status !== 200) { // Catch Shopify Cart API errors
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId,
              errors: response.description || response.message,
              message: response.message,
            });
            this.handleErrorMessage(response.description || response.message);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId,
          });

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => {
                this.cart.renderContents(response);
              });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((error) => {
          console.error(error);
          this.error = true;
          this.handleErrorMessage('Something went wrong while adding this item to the cart. Please try again.');
        })
        .finally(() => {
          this.resetSubmitButton();
        });
    }

    buildDefaultRequestConfig(formData) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;
      return config;
    }

    buildMultiItemRequestConfig(formData, panelPayload) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      
      // CRITICAL: Explicitly set Content-Type for JSON payload so Shopify doesn't ignore the array
      config.headers['Content-Type'] = 'application/json';
      config.headers['Accept'] = 'application/json';

      const body = {
        items: [
          this.buildMainCartItem(formData, panelPayload.mainProperties || {}),
          ...panelPayload.items,
        ],
      };

      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = JSON.stringify(body);
      return config;
    }

    buildMainCartItem(formData, extraProperties) {
      const item = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity') || 1),
      };

      const properties = this.getLineItemProperties(formData);
      Object.assign(properties, extraProperties || {});

      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        item.selling_plan = sellingPlan;
      }

      return item;
    }

    getLineItemProperties(formData) {
      const properties = {};

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)]$/);
        if (match && value !== '') {
          properties[match[1]] = value;
        }
      });

      return properties;
    }

    getPanelConfiguratorPayload(formData) {
      const postRoot = document.querySelector('[data-sf-post-configurator-root]');

      if (
        postRoot &&
        postRoot.sfPostConfigurator &&
        typeof postRoot.sfPostConfigurator.buildCartPayload === 'function'
      ) {
        return postRoot.sfPostConfigurator.buildCartPayload(formData);
      }

      const configuratorRoot = this.findPanelConfiguratorRoot();
      const configurator = configuratorRoot && configuratorRoot.sfPanelConfigurator;

      if (!configurator || typeof configurator.buildCartPayload !== 'function') {
        return null;
      }

      return configurator.buildCartPayload(formData);
    }

    findPanelConfiguratorRoot() {
      const roots = Array.from(document.querySelectorAll('[data-sf-panel-configurator]'));
      
      // CRITICAL FIX: If there is only one configurator on the page, always return it.
      // This prevents silent failures caused by sticky/mobile add-to-cart form ID mismatches.
      if (roots.length === 1) {
        return roots[0];
      }

      if (!this.form || !this.form.id) return null;
      return roots.find((root) => root.dataset.productFormId === this.form.id) || null;
    }

    resetSubmitButton() {
      this.submitButton.classList.remove('loading');

      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      if (!this.error) {
        this.submitButton.removeAttribute('aria-disabled');
      } else {
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;

      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}